<template>
  <div class="container mx-auto px-4 py-8 bg-gray-50">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold mb-6 text-center text-customHafadhColor">تقويم جلسات الدارس</h1>
      
      <!-- Student and Date Selection -->
      <div class="flex justify-center gap-4 items-center mb-6 flex-wrap">
        <select 
          v-model="selectedStudentId"
          @change="handleStudentChange"
          class="block w-64 px-4 py-2 bg-white border-2 border-customHafadhColor rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor">
          <option value="">اختر الدارس</option>
          <option v-for="student in students" :key="student.id" :value="student.id">
            {{ student.fullName }}
          </option>
        </select>
        
        <input
          type="month"
          v-model="selectedMonth"
          @change="fetchStudentSchedule"
          class="block px-4 py-2 border-2 border-customHafadhColor rounded-lg focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
        />
        
        <button
          v-if="selectedStudentId"
          @click="exportStudentSchedule"
          class="px-6 py-2 bg-customHafadhColor text-white rounded-lg hover:bg-customHafadhColor/90 transition-colors duration-300 flex items-center gap-2"
          :disabled="loading"
        >
          <i class="fas fa-download"></i>
          تصدير الجدول
        </button>
      </div>

      <!-- Student Info -->
      <div v-if="selectedStudent" class="bg-white rounded-lg p-4 mb-6 shadow-md">
        <div class="flex items-center gap-4">
          <div class="flex-1">
            <h3 class="text-xl font-bold text-customHafadhColor">{{ selectedStudent.fullName }}</h3>
            <p v-if="scheduleData" class="text-gray-600">
              المعلم: {{ scheduleData.teacherName || 'غير محدد' }} | 
              الجلسة: {{ scheduleData.sessionName || 'غير محدد' }}
            </p>
          </div>
          <div class="text-right">
            <span class="text-sm text-gray-500">عدد الجلسات هذا الشهر</span>
            <p class="text-2xl font-bold text-customHafadhColor">{{ totalSessionsThisMonth }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-customHafadhColor border-t-transparent"></div>
      <p class="mt-4 text-lg text-customHafadhColor">جاري التحميل...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg relative text-right mb-4">
      <strong class="font-bold ml-2">خطأ!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Calendar View -->
    <div v-if="selectedStudentId && !loading" class="bg-white rounded-lg shadow-lg overflow-hidden">
      <!-- Calendar Header -->
      <div class="bg-customHafadhColor text-white p-4">
        <div class="flex justify-between items-center">
          <button @click="previousMonth" class="p-2 hover:bg-white/20 rounded">
            <i class="fas fa-chevron-right"></i>
          </button>
          <h2 class="text-xl font-bold">{{ formatMonthYear(currentDate) }}</h2>
          <button @click="nextMonth" class="p-2 hover:bg-white/20 rounded">
            <i class="fas fa-chevron-left"></i>
          </button>
        </div>
      </div>

      <!-- Days of Week Header -->
      <div class="grid grid-cols-7 bg-gray-100">
        <div v-for="day in daysOfWeek" :key="day" class="p-3 text-center font-semibold text-gray-700 border-b">
          {{ day }}
        </div>
      </div>

      <!-- Calendar Days -->
      <div class="grid grid-cols-7">
        <div 
          v-for="date in calendarDays" 
          :key="date.dateString"
          class="min-h-32 border-b border-r border-gray-200 p-2"
          :class="{
            'bg-gray-50': !date.isCurrentMonth,
            'bg-blue-50': date.isToday,
            'cursor-pointer hover:bg-gray-50': date.isCurrentMonth
          }"
          @click="selectDate(date)"
        >
          <!-- Date Number -->
          <div class="flex justify-between items-start mb-2">
            <span 
              class="text-sm font-semibold"
              :class="{
                'text-gray-400': !date.isCurrentMonth,
                'text-blue-600': date.isToday,
                'text-gray-800': date.isCurrentMonth && !date.isToday
              }"
            >
              {{ date.day }}
            </span>
            <span v-if="getSessionsForDate(date.dateString).length > 0" 
                  class="bg-customHafadhColor text-white text-xs px-2 py-1 rounded-full">
              {{ getSessionsForDate(date.dateString).length }}
            </span>
          </div>

          <!-- Sessions for this date -->
          <div class="space-y-1">
            <div 
              v-for="session in getSessionsForDate(date.dateString).slice(0, 3)" 
              :key="session.id"
              class="bg-customHafadhColor/10 text-customHafadhColor text-xs p-1 rounded truncate"
              :title="`${formatTime(session.time)} - ${session.teacherName}`"
            >
              {{ formatTime(session.time) }}
            </div>
            <div v-if="getSessionsForDate(date.dateString).length > 3" 
                 class="text-xs text-gray-500">
              +{{ getSessionsForDate(date.dateString).length - 3 }} أخرى
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Day Details Modal -->
    <div v-if="selectedDateDetails" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-96 max-w-md mx-4 max-h-96 overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-customHafadhColor">
            جلسات يوم {{ formatDate(selectedDateDetails.dateString) }}
          </h3>
          <button @click="selectedDateDetails = null" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div v-if="getSessionsForDate(selectedDateDetails.dateString).length === 0" 
             class="text-center py-8 text-gray-500">
          لا توجد جلسات في هذا اليوم
        </div>
        
        <div v-else class="space-y-3">
          <div 
            v-for="session in getSessionsForDate(selectedDateDetails.dateString)" 
            :key="session.id"
            class="bg-gray-50 p-3 rounded-lg"
          >
            <div class="flex justify-between items-center">
              <div>
                <p class="font-semibold">{{ formatTime(session.time) }}</p>
                <p class="text-sm text-gray-600">المعلم: {{ session.teacherName || 'غير محدد' }}</p>
                <p class="text-xs text-gray-500">{{ session.prayerName }}</p>
                <div class="flex gap-2 mt-2">
                  <span 
                    v-if="session.isStudentPresent" 
                    class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded"
                  >
                    حضر الدارس
                  </span>
                  <span 
                    v-if="session.isTeacherPresent" 
                    class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                  >
                    حضر المعلم
                  </span>
                </div>
              </div>
              <div class="flex gap-2">
                <button 
                  @click="editSession(session)"
                  class="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600"
                >
                  تعديل
                </button>
                <button 
                  @click="cancelSession(session)"
                  class="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Student Selected -->
    <div v-else-if="!selectedStudentId && !loading" class="text-center py-12 text-gray-500 text-lg">
      الرجاء اختيار طالب لعرض جدوله الزمني
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'StudentCalendar',
  data() {
    return {
      students: [],
      selectedStudentId: '',
      selectedStudent: null,
      selectedMonth: new Date().toISOString().slice(0, 7), // YYYY-MM format
      currentDate: new Date(),
      studentSchedule: [],
      scheduleData: null,
      loading: false,
      error: null,
      selectedDateDetails: null,
      daysOfWeek: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
    };
  },
  computed: {
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      
      // First day of the month
      const firstDay = new Date(year, month, 1);
      
      // First day of the calendar (might be from previous month)
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      // Generate 42 days (6 weeks)
      const days = [];
      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        
        days.push({
          day: date.getDate(),
          dateString: date.toISOString().split('T')[0],
          isCurrentMonth: date.getMonth() === month,
          isToday: this.isToday(date)
        });
      }
      
      return days;
    },
    totalSessionsThisMonth() {
      const [year, month] = this.selectedMonth.split('-');
      return this.studentSchedule.filter(session => {
        const sessionDate = new Date(session.date);
        return sessionDate.getFullYear() == year && 
               sessionDate.getMonth() == month - 1;
      }).length;
    }
  },
  methods: {
    async fetchStudents() {
      try {
        this.loading = true;
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.get_enabled_students,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        if (response.data?.result?.data) {
          this.students = response.data.result.data;
        }
      } catch (err) {
        this.error = 'حدث خطأ أثناء تحميل قائمة الطلاب';
        console.error('Error fetching students:', err);
      } finally {
        this.loading = false;
      }
    },
    async handleStudentChange() {
      if (this.selectedStudentId) {
        this.selectedStudent = this.students.find(s => s.id == this.selectedStudentId);
        await this.fetchStudentSchedule();
      } else {
        this.selectedStudent = null;
        this.studentSchedule = [];
        this.scheduleData = null;
      }
    },
    async fetchStudentSchedule() {
      if (!this.selectedStudentId) return;
      
      try {
        this.loading = true;
        this.error = null;
        
        // Use the new endpoint for monthly schedule
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + 
          url.student_monthly_schedule + 
          `/${this.selectedStudentId}?month=${this.selectedMonth}`,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        
        if (response.data?.result?.data) {
          const scheduleData = response.data.result.data;
          this.scheduleData = scheduleData;
          
          // Convert the schedule data to the format expected by the component
          const sessions = [];
          
          Object.entries(scheduleData.dailySchedule || {}).forEach(([date, daySessions]) => {
            daySessions.forEach(session => {
              sessions.push({
                id: `${date}-${session.time}-${session.dailyTrackerId || Math.random()}`,
                date: date,
                time: session.time,
                teacherName: session.teacherName,
                teacherId: session.teacherId,
                dailyTrackerId: session.dailyTrackerId,
                prayerName: session.prayerName,
                isStudentPresent: session.isStudentPresent || false,
                isTeacherPresent: session.isTeacherPresent || false
              });
            });
          });
          
          this.studentSchedule = sessions;
        } else {
          this.studentSchedule = [];
          this.scheduleData = null;
        }
      } catch (err) {
        this.error = 'حدث خطأ أثناء تحميل جدول الدارس';
        console.error('Error fetching student schedule:', err);
        this.studentSchedule = [];
        this.scheduleData = null;
      } finally {
        this.loading = false;
      }
    },
    getSessionsForDate(dateString) {
      return this.studentSchedule.filter(session => session.date === dateString);
    },
    selectDate(date) {
      if (date.isCurrentMonth && this.getSessionsForDate(date.dateString).length > 0) {
        this.selectedDateDetails = date;
      }
    },
    previousMonth() {
      this.currentDate.setMonth(this.currentDate.getMonth() - 1);
      this.selectedMonth = this.currentDate.toISOString().slice(0, 7);
      this.fetchStudentSchedule();
    },
    nextMonth() {
      this.currentDate.setMonth(this.currentDate.getMonth() + 1);
      this.selectedMonth = this.currentDate.toISOString().slice(0, 7);
      this.fetchStudentSchedule();
    },
    isToday(date) {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    },
    formatMonthYear(date) {
      const months = [
        'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
        'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
      ];
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      return `${days[date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    },
    formatTime(time) {
      if (!time) return '';
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    editSession(session) {
      // Emit event or navigate to edit page
      this.$router.push({
        name: 'SessionReport',
        query: {
          studentId: this.selectedStudentId,
          date: session.date,
          dailyTrackerId: session.dailyTrackerId
        }
      });
    },
    async cancelSession(session) {
      if (confirm(`هل أنت متأكد من إلغاء جلسة ${this.formatTime(session.time)}؟`)) {
        // Implementation for canceling session
        console.log('Cancel session:', session);
      }
    },
    async exportStudentSchedule() {
      // Implementation for exporting schedule
      console.log('Export student schedule for:', this.selectedStudent?.fullName);
    }
  },
  created() {
    this.fetchStudents();
  },
  watch: {
    selectedMonth() {
      const [year, month] = this.selectedMonth.split('-');
      this.currentDate = new Date(year, month - 1, 1);
    }
  }
};
</script>

<style scoped>
.text-customHafadhColor {
  color: #c9a06c;
}

.bg-customHafadhColor {
  background-color: #c9a06c;
}

.border-customHafadhColor {
  border-color: #c9a06c;
}

.focus\:ring-customHafadhColor:focus {
  --tw-ring-color: #c9a06c;
}

.hover\:bg-customHafadhColor\/90:hover {
  background-color: rgba(201, 160, 108, 0.9);
}

.bg-customHafadhColor\/10 {
  background-color: rgba(201, 160, 108, 0.1);
}

/* RTL Support */
.container {
  direction: rtl;
}

/* Calendar specific styles */
.min-h-32 {
  min-height: 8rem;
}

/* Custom scrollbar for modal */
.max-h-96::-webkit-scrollbar {
  width: 6px;
}

.max-h-96::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.max-h-96::-webkit-scrollbar-thumb {
  background: #c9a06c;
  border-radius: 3px;
}

.max-h-96::-webkit-scrollbar-thumb:hover {
  background: #b08d5d;
}
</style>

